import type { PayoutView } from "@/services/types";
import { Badge } from "@/components/base/Badge";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDate } from "@/utils/format";

/**
 * Cycle context — shows where the prize amount came from so it is verifiable
 * rather than appearing from nowhere.
 */
export function CycleContextPanel({ payout }: { payout: PayoutView }) {
  const showDividend = !payout.isOrganizerReserved;
  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <div className="border-b border-background-100 px-4 py-3">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground-900">
          <i className="ri-archive-line text-primary-600" />
          Cycle context
        </h3>
      </div>
      <div className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-medium text-foreground-900">{payout.groupName}</p>
          <span className="tnum text-xs text-foreground-400">{payout.groupCode}</span>
          <Badge color="secondary" dot>Cycle {payout.cycle} · {payout.periodLabel}</Badge>
          {payout.isOrganizerReserved ? (
            <Badge color="accent" dot>Reserved organizer cycle</Badge>
          ) : null}
        </div>
        <dl className="mt-3 space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <dt className="text-foreground-600">Full pool</dt>
            <dd className="tnum font-medium text-foreground-900"><CurrencyDisplay amount={payout.pool} hideSymbol /></dd>
          </div>
          {payout.isOrganizerReserved ? (
            <div className="flex items-center justify-between">
              <dt className="text-foreground-600">Cycle commission</dt>
              <dd className="tnum font-semibold text-primary-700"><CurrencyDisplay amount={payout.commissionAmount ?? 0} hideSymbol /></dd>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <dt className="text-foreground-600">Winning bid</dt>
              <dd className="tnum font-semibold text-accent-800"><CurrencyDisplay amount={payout.prize} hideSymbol /></dd>
            </div>
          )}
          <div className="flex items-center justify-between">
            <dt className="text-foreground-600">Discount generated</dt>
            <dd className="tnum font-medium text-danger-600"><CurrencyDisplay amount={payout.discount} hideSymbol /></dd>
          </div>
          {showDividend ? (
            <div className="flex items-center justify-between">
              <dt className="text-foreground-600">Dividend / member</dt>
              <dd className="tnum font-medium text-foreground-900"><CurrencyDisplay amount={payout.perMemberDividend} hideSymbol /></dd>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <dt className="text-foreground-600">Dividend / member</dt>
              <dd className="tnum font-medium text-foreground-400">0 — no discount</dd>
            </div>
          )}
        </dl>
        <p className="mt-3 text-xs text-foreground-500">
          {payout.isOrganizerReserved
            ? "Reserved cycle: the organizer takes the full pool with no discount, so every member pays the full installment."
            : "The winning bid is the amount this member agreed to receive in place of the full pool."}
        </p>
      </div>
    </div>
  );
}